import React, { useMemo } from 'react';
import { Text } from "@hangar/web-components/core/Text";
import * as S from './styles/ItineraryComparison.style';
import { ItineraryComparisonViewModel } from '../../services/api/flightChanges/types';
import { ItinerarySegment } from './ItinerarySegment';
import { PartializedLoadMessage } from './PartializedLoadMessage';
import { CompleteLoadMessage } from './CompleteLoadMessage';
import { PartializedLoadInTransitMessage } from './PartializedLoadInTransitMessage';
import { PartializedLoadsList } from './PartializedLoadsList';
import { useTranslation } from '../../hooks/i18n';

export interface ItineraryComparisonProps {
    data: ItineraryComparisonViewModel;
}

const ItineraryComparisonComponent: React.FC<ItineraryComparisonProps> = ({ data }) => {
    const { t } = useTranslation();
    
    const title = useMemo(() => t('itinerary.title'), [t]);
    const previousLabel = useMemo(() => t('itinerary.previous'), [t]);
    const newLabel = useMemo(() => t('itinerary.new'), [t]);
    const emptyPreviousMessage = useMemo(() => t('itinerary.emptyPrevious'), [t]);
    const emptyNewMessage = useMemo(() => t('itinerary.emptyNew'), [t]);
    
    return (
        <S.ItineraryCard 
            role="region" 
            aria-label={title}
        >
            <S.SectionTitleText>
                <Text variant="heading-md">{title}</Text>
            </S.SectionTitleText>
            <S.ItineraryColumns>
                <S.ItineraryColumn 
                    role="group" 
                    aria-label={previousLabel}
                >
                    <S.ItineraryLabel>{previousLabel}</S.ItineraryLabel>
                    {data.isOriginalPartialized && data.originalPartializedLoads ? (
                        <PartializedLoadsList loads={data.originalPartializedLoads} variant="previous" />
                    ) : (
                        <S.FlightSegmentsContainer role="list">
                            {data.previous.length > 0 ? (
                                data.previous.map((segment, index) => (
                                    <ItinerarySegment
                                        key={`previous-${segment.leg}-${segment.flightNumber}`}
                                        segment={segment}
                                        variant="previous"
                                        isFirst={index === 0}
                                        isLast={index === data.previous.length - 1}
                                    />
                                ))
                            ) : (
                                <S.EmptyMessage role="status" aria-live="polite">
                                    {emptyPreviousMessage}
                                </S.EmptyMessage>
                            )}
                        </S.FlightSegmentsContainer>
                    )}
                </S.ItineraryColumn>

                <S.ItineraryColumn 
                    role="group" 
                    aria-label={newLabel}
                >
                    <S.ItineraryLabel>{newLabel}</S.ItineraryLabel>
                    {data.isCompleteLoad && <CompleteLoadMessage />}
                    {data.isPartializedInTransit && <PartializedLoadInTransitMessage />}
                    {data.isPartialized && !data.isCompleteLoad && !data.isPartializedInTransit && <PartializedLoadMessage />}
                    {data.isPartialized && data.partializedLoads ? (
                        <PartializedLoadsList loads={data.partializedLoads} />
                    ) : (
                        <S.FlightSegmentsContainer role="list">
                            {data.new.length > 0 ? (
                                data.new.map((segment, index) => (
                                    <ItinerarySegment
                                        key={`new-${segment.leg}-${segment.flightNumber}`}
                                        segment={segment}
                                        variant="new"
                                        isFirst={index === 0}
                                        isLast={index === data.new.length - 1}
                                    />
                                ))
                            ) : (
                                <S.EmptyMessage role="status" aria-live="polite">
                                    {emptyNewMessage}
                                </S.EmptyMessage>
                            )}
                        </S.FlightSegmentsContainer>
                    )}
                </S.ItineraryColumn>
            </S.ItineraryColumns>
        </S.ItineraryCard>
    );
};

export const ItineraryComparison = React.memo(ItineraryComparisonComponent, (prevProps, nextProps) => {
    const previousEqual = 
        prevProps.data.previous.length === nextProps.data.previous.length &&
        prevProps.data.previous.every((prev, index) => {
            const next = nextProps.data.previous[index];
            return (
                prev.leg === next.leg &&
                prev.flightNumber === next.flightNumber &&
                prev.originAirport === next.originAirport &&
                prev.destinationAirport === next.destinationAirport
            );
        });
    
    const newEqual = 
        prevProps.data.new.length === nextProps.data.new.length &&
        prevProps.data.new.every((prev, index) => {
            const next = nextProps.data.new[index];
            return (
                prev.leg === next.leg &&
                prev.flightNumber === next.flightNumber &&
                prev.originAirport === next.originAirport &&
                prev.destinationAirport === next.destinationAirport
            );
        });
    
    const partializedEqual = 
        prevProps.data.isPartialized === nextProps.data.isPartialized &&
        prevProps.data.partializedLoads?.length === nextProps.data.partializedLoads?.length;
    
    const originalPartializedEqual = 
        prevProps.data.isOriginalPartialized === nextProps.data.isOriginalPartialized &&
        prevProps.data.originalPartializedLoads?.length === nextProps.data.originalPartializedLoads?.length;
    
    const completeLoadEqual = 
        prevProps.data.isCompleteLoad === nextProps.data.isCompleteLoad;
    
    const partializedInTransitEqual = 
        prevProps.data.isPartializedInTransit === nextProps.data.isPartializedInTransit;
    
    return previousEqual && newEqual && partializedEqual && originalPartializedEqual && completeLoadEqual && partializedInTransitEqual;
});